"use client";

import React, { createContext, useContext, useState, ReactNode } from 'react';

interface CartItem {
  code: string;
  name: string;
  type: string;
  image: string;
  alt: string;
  quantity: number;
  price?: number;
}

interface CartContextType {
  cart: CartItem[];
  addToCart: (product: Omit<CartItem, 'quantity'>) => void;
  removeFromCart: (code: string) => void;
  updateQuantity: (code: string, quantity: number) => void;
  clearCart: () => void;
  getCartTotal: () => number;
  getCartItemCount: () => number;
  checkoutWhatsApp: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<CartItem[]>([]);

  const addToCart = (product: Omit<CartItem, 'quantity'>) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find((item) => item.code === product.code);
      if (existingItem) {
        return prevCart.map((item) =>
          item.code === product.code
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prevCart, { ...product, quantity: 1 }];
    });
  };

  const removeFromCart = (code: string) => {
    setCart((prevCart) => prevCart.filter((item) => item.code !== code));
  };

  const updateQuantity = (code: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(code);
      return;
    }
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.code === code ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      const itemPrice = item.price || 0;
      return total + itemPrice * item.quantity;
    }, 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const checkoutWhatsApp = () => {
    // Verify cart state is not empty, undefined, or null
    if (!cart || cart.length === 0) {
      alert('Tu carrito está vacío');
      return;
    }

    // Build message with proper formatting
    let message = '¡Hola LS Protection! Me interesa realizar este pedido:\n\n';

    // Format each cart item with name, quantity, and subtotal
    cart.forEach((item) => {
      const itemPrice = item.price || 0;
      const subtotal = itemPrice * item.quantity;
      
      message += `• ${item.name}\n`;
      message += `  Cantidad: ${item.quantity}\n`;
      
      if (itemPrice > 0) {
        message += `  Subtotal: RD$ ${subtotal.toFixed(2)}\n`;
      }
      message += `\n`;
    });

    // Calculate and add total
    const total = getCartTotal();
    if (total > 0) {
      message += `Total: RD$ ${total.toFixed(2)}`;
    }

    // Use encodeURIComponent to properly encode spaces and special characters
    const encodedMessage = encodeURIComponent(message);
    
    // Use standard wa.me format with phone number instead of wa.link shortener
    // wa.link/4exlpx corresponds to a phone number - using standard format
    const whatsappUrl = `https://wa.me/18096318952?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartItemCount,
        checkoutWhatsApp,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};